import { UserData, UserDataForSubAccount } from '../utils/types'

/**
 * Why this exists SEPARATELY from the failure sentinels
 * -----------------------------------------------------
 * The sentinel path (`isFailedCall`) catches reads that announced themselves as
 * failures. This catches the ones that did not: a decodable-but-wrong response,
 * a market whose metadata went missing so its price resolved to `undefined`, a
 * converter that divided by a zero it should never have seen. Those produce the
 * SAME user-visible artefact as a dropped read — a debt with no collateral, a
 * `NaN` health factor — while every slot reports success.
 *
 * So this is the last gate before a position is published: it asserts what must
 * be true of any real lending position, independent of how the data was
 * obtained.
 */

/** One failed assertion about a sub-account's published shape. */
export interface InvariantViolation {
  /** Sub-account this fired on (`accountId`). */
  accountId: string
  /** Machine-readable check name. */
  code: 'non-finite' | 'debt-without-collateral' | 'invalid-mode'
  /** Human-readable detail for logs. */
  detail: string
  /**
   * `true` when the violation is only conclusive because the read was also
   * known-incomplete (see {@link validateUserData}).
   */
  requiresFailedReads: boolean
}

/** Numeric fields that can never legitimately be NaN or ±Infinity. */
const BALANCE_FIELDS = [
  'deposits',
  'debt',
  'nav',
  'collateral',
  'adjustedDebt',
  'borrowDiscountedCollateral',
] as const

const APR_FIELDS = ['apr', 'borrowApr', 'depositApr'] as const

const isBadNumber = (v: unknown): boolean =>
  typeof v === 'number' && !Number.isFinite(v)

/**
 * Collects every invariant violation on one sub-account.
 *
 * Nothing is decided here — {@link validateUserData} owns the drop/keep call,
 * because whether `debt-without-collateral` is fatal depends on context this
 * function does not have.
 */
function checkSubAccount(entry: UserDataForSubAccount): InvariantViolation[] {
  const violations: InvariantViolation[] = []
  const accountId = entry?.accountId ?? '0'
  const balance = entry?.balanceData as any
  const apr = entry?.aprData as any

  // 1. Non-finite arithmetic. `NaN`/`Infinity` has no legitimate source in a
  //    position: it means a missing price, a missing decimals, or a division by
  //    a zero denominator that a complete read would have made non-zero. It is
  //    also what renders as "MODE #NaN" / "$NaN" if it escapes.
  for (const field of BALANCE_FIELDS) {
    if (isBadNumber(balance?.[field]))
      violations.push({
        accountId,
        code: 'non-finite',
        detail: `balanceData.${field} = ${balance[field]}`,
        requiresFailedReads: false,
      })
  }
  for (const field of APR_FIELDS) {
    if (isBadNumber(apr?.[field]))
      violations.push({
        accountId,
        code: 'non-finite',
        detail: `aprData.${field} = ${apr[field]}`,
        requiresFailedReads: false,
      })
  }
  if (isBadNumber(entry?.health))
    violations.push({
      accountId,
      code: 'non-finite',
      detail: `health = ${entry.health}`,
      requiresFailedReads: false,
    })
  if (isBadNumber(entry?.borrowCapacityUSD))
    violations.push({
      accountId,
      code: 'non-finite',
      detail: `borrowCapacityUSD = ${entry.borrowCapacityUSD}`,
      requiresFailedReads: false,
    })

  // 2. The selected mode must survive a round-trip to the client as an id.
  //    `Number('0x')` is `NaN`, and a stringified `NaN` renders literally.
  const mode = entry?.userConfig?.selectedMode
  if (mode !== undefined && mode !== null && String(mode).includes('NaN'))
    violations.push({
      accountId,
      code: 'invalid-mode',
      detail: `userConfig.selectedMode = ${String(mode)}`,
      requiresFailedReads: false,
    })

  // 3. Debt with no collateral behind it. This cannot be produced by a complete
  //    read of a live position under ANY lender — you cannot borrow against
  //    nothing. It is deliberately NOT restricted to cross-margin lenders: an
  //    isolated Morpho or Silo market derives its health from a collateral read
  //    and a debt read, so losing the collateral one leaves exactly this shape.
  //    Isolated is not the same as single-read.
  //
  //    It CAN be produced by a complete read of a DEAD position: a fully
  //    liquidated account can carry residual bad debt against zero collateral.
  //    That is real and must stay visible, which is why the check is conclusive
  //    only alongside known failed reads.
  if ((balance?.debt ?? 0) > 0 && (balance?.deposits ?? 0) <= 0)
    violations.push({
      accountId,
      code: 'debt-without-collateral',
      detail: `debt = ${balance?.debt}, deposits = ${balance?.deposits}`,
      requiresFailedReads: true,
    })

  return violations
}

export interface ValidationResult {
  /** Sub-accounts that passed. Empty means the whole entry must be dropped. */
  kept: UserDataForSubAccount[]
  /** Every violation found, including ones on kept sub-accounts. */
  violations: InvariantViolation[]
  /** Sub-account ids dropped as corrupt. */
  dropped: string[]
}

/**
 * Validates a converted entry and drops the sub-accounts that cannot be true.
 *
 * `hadFailedReads` gates the checks whose violation is ambiguous on its own:
 * with a known-incomplete read, `debt-without-collateral` is the signature of a
 * dropped collateral slot and the sub-account is corrupt; with a clean read it
 * is a genuine (if grim) position and is kept. Unconditional checks — anything
 * non-finite — fire either way, because no read produces those legitimately.
 */
export function validateUserData(
  userData: UserData,
  hadFailedReads: boolean,
): ValidationResult {
  const kept: UserDataForSubAccount[] = []
  const dropped: string[] = []
  const violations: InvariantViolation[] = []

  for (const entry of userData?.data ?? []) {
    const found = checkSubAccount(entry)
    violations.push(...found)
    const fatal = found.filter((v) => !v.requiresFailedReads || hadFailedReads)
    if (fatal.length > 0) dropped.push(entry?.accountId ?? '0')
    else kept.push(entry)
  }

  return { kept, violations, dropped }
}
